import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import InviteJoin from "../components/InviteJoin";
import ChatWindow from "../components/ChatWindow";
import BottomTabs from "../components/BottomTabs";
import UserInfoOverlay from "../components/UserInfoOverlay";

export default function MobileChatPanel({
  mobileView,
  activeTab,
  TABS,
  selectedItem,
  inviteToken,
  socket,
  myChatId,
  myUserName,
  handleInviteSuccess,
  handleInviteFailure,
  interactionsBadgeCount,
  notificationsBadgeCount,
   feedBadgeCount = 0,
  uploadImage,

  // chat window props
  messages,
  setMessages,
  typingUsers,
  groupTyping,
  groupMembers,
  groups,
  setGroupMembers,
  chatMessages,
  chatKey,
  users,
  setUsers,
  setGroups,
  currentUser,
  fallbackItem,
  input,
  setInput,
  selectedReply,
  chatInstances,
  activeChatKey,
  setSelectedReply,
  typingTimeoutRef,
  getUserKey,
  getGroupKey,
  pendingMedia,
  setPendingMedia,
  isUploading,
  setIsUploading,
  uploadProgress,
  setUploadProgress,
  p2pOrders,
  orderTimers,
  activeP2POrder,
  feedSocket,
  showToast,
  scheduledMessages,
  onConfirmPayment,

  // selected item
  setSelectedItem,

  // avatar modal (still passed to ChatWindow if needed internally)
  avatarZoom,
  setAvatarZoom,

  // bottom tabs
  isPostPage,
  MOBILE_PRIMARY_TABS,
  isSocialView,
  setActiveTab,
  openConnect,

  // 🔑 E2EE
  cryptoFns,
}) {
  // Own-profile overlay — Profile tab opens this instead of navigating
  // straight into My Posts. Posts/edit/delete are rendered inside via the
  // shared AuthorPostsContext (see GroupChat.js renderAuthorPosts), which
  // already shows the 3-dot menu and Earnings (not Support) for your own
  // posts because it compares post.author.chatId to myChatId.
  // (Declared before the mobileView guard below — hooks must run on every render.)
  const [showProfile, setShowProfile] = useState(false);
  const bottomTabsRef = useRef(null);
   const navigate = useNavigate();
  const location = useLocation();

  // Chats-tab badge: number of chats with unread messages.
  // Hidden (0) whenever the user is on the Chats tab.
 const onChatsTab =
  activeTab === TABS.CHATS ||
  activeTab === TABS.CONNECT ||
  activeTab === TABS.BUY_DATA;
  const chatsBadgeCount = onChatsTab
    ? 0
    : [...(users || []), ...(groups || [])].filter((c) => (c.unreadCount || 0) > 0).length;

  // Publish the tab bar's real height so ChatInput.js can dock flush.
  // When a chat is open, remove the tab height entirely.
  useEffect(() => {
    const chatOpen = activeTab === TABS.CHATS && selectedItem;

    if (chatOpen) {
      document.documentElement.style.setProperty("--bottom-tabs-h", "0px");
      return;
    }

    const el = bottomTabsRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;

    const publish = () => {
      document.documentElement.style.setProperty(
        "--bottom-tabs-h",
        `${el.offsetHeight}px`
      );
    };

    publish();

    const ro = new ResizeObserver(publish);
    ro.observe(el);

    return () => ro.disconnect();
  }, [mobileView, activeTab, selectedItem, TABS]);


  // ✅ Prevent rendering on desktop
  if (!mobileView) return null;

  return (
    <>
      {/* ================= MOBILE — CHAT WINDOW ================= */}
      <div
        className="absolute inset-0 z-20"
        style={{
          visibility:
            activeTab === TABS.CHATS && selectedItem ? "visible" : "hidden",
          pointerEvents:
            activeTab === TABS.CHATS && selectedItem ? "auto" : "none",
        }}
      >
        {inviteToken ? (
          <InviteJoin
            socket={socket}
            chatId={myChatId}
            userName={myUserName}
            inviteToken={inviteToken}
            onSuccess={handleInviteSuccess}
            onFailure={handleInviteFailure}
          />
        ) : (
          <div className="relative h-full w-full">
            {Object.values(chatInstances || {}).map((chat) => (
              <div
                key={chat.key}
                className="absolute inset-0"
                style={{
                  display: chat.key === activeChatKey ? "block" : "none",
                }}
              >
                <ChatWindow
                  selectedItem={chat.item}
                  isActive={chat.key === activeChatKey}

                  setSelectedItem={setSelectedItem}
                  activeTab={activeTab}

                  messages={messages}
                  setMessages={setMessages}

                  typingUsers={typingUsers}
                  groupTyping={groupTyping}

                  groupMembers={groupMembers}
                  groups={groups}
                  users={users}

                  setGroupMembers={setGroupMembers}
                  setGroups={setGroups}
                  setUsers={setUsers}

                  setActiveTab={setActiveTab}
                  TABS={TABS}

                  chatMessages={messages[chat.key] || []}
                  chatKey={chat.key}

                  currentUser={currentUser}
                  socket={socket}

                  input={input}
                  setInput={setInput}

                  fallbackItem={chat.item}

                  selectedReply={selectedReply}
                  setSelectedReply={setSelectedReply}

                  mobileView={mobileView}

                  typingTimeoutRef={typingTimeoutRef}

                  getUserKey={getUserKey}
                  getGroupKey={getGroupKey}

                  pendingMedia={pendingMedia}
                  setPendingMedia={setPendingMedia}

                  isUploading={isUploading}
                  setIsUploading={setIsUploading}

                  uploadProgress={uploadProgress}
                  setUploadProgress={setUploadProgress}

                  p2pOrders={p2pOrders}
                  onConfirmPayment={onConfirmPayment}
                  orderTimers={orderTimers}
                  activeP2POrder={activeP2POrder}

                  feedSocket={feedSocket}
                  showToast={showToast}

                  scheduledMessages={scheduledMessages}
                  cryptoFns={cryptoFns}

                  setAvatarZoom={setAvatarZoom}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ================= MOBILE BOTTOM TABS ================= */}
      {!(activeTab === TABS.CHATS && selectedItem) && (
        <div
          ref={bottomTabsRef}
          className="
            bottom-tabs-bar
            fixed bottom-0 left-0 right-0 z-[60]
            bg-white dark:bg-neutral-800
            border-t border-neutral-200 dark:border-neutral-700
            md:hidden
          "
        
style={{
  paddingBottom: "env(safe-area-inset-bottom, 0px)",
}}

        >
          <BottomTabs
           variant={
  isPostPage ||
  activeTab === TABS.FEED ||
  activeTab === TABS.MOMENT ||
  activeTab === TABS.MY_POSTS ||
  activeTab === TABS.INTERACTIONS ||
  activeTab === TABS.NOTIFICATIONS
    ? "home"
    : activeTab === TABS.ASSETS || activeTab === TABS.TRANSACTIONS
    ? "asset"
    : "default"
}
            isPostPage={isPostPage}
           activeTab={
  showProfile
    ? TABS.MY_POSTS
    : activeTab === TABS.INTERACTIONS ||
      activeTab === TABS.NOTIFICATIONS
    ? TABS.FEED
    : activeTab === TABS.CONNECT || activeTab === TABS.BUY_DATA
    ? TABS.CHATS
    : activeTab
}
            onChangeTab={(tab) => {
              setShowProfile(false);

              if (isPostPage) {
                navigate(location.pathname, {
                  replace: true,
                  state: {},
                });
              }

              setActiveTab(tab);
            }}
            onOpenConnect={openConnect}
            onOpenProfile={() => setShowProfile(true)}
            interactionCount={interactionsBadgeCount}
            notificationCount={notificationsBadgeCount}
             chatCount={chatsBadgeCount}
             feedCount={feedBadgeCount}
          />
        </div>
      )}

      {/* ================= OWN PROFILE OVERLAY ================= */}
      <UserInfoOverlay
        open={showProfile}
        onClose={() => setShowProfile(false)}
        user={{ ...currentUser, chatId: myChatId }}
        myChatId={myChatId}
        mutualGroups={(groups || []).filter((g) => {
          const mem = groupMembers?.[g.groupId] || g.members || [];
          return Array.isArray(mem) && mem.some((m) => String(m.chatId) === String(myChatId));
        })}
        onOpenGroup={(g) => {
              setShowProfile(false);
          setSelectedItem({
            type: "group",
            groupId: g.groupId,
            name: g.name,
            groupImageUrl: g.groupImageUrl,
          });
          setActiveTab(TABS.CHATS);
        }}
      />
    </>
  );
}